// composables/useRealtimeMessages.js - FIXED with proper image data handling
import { ref, readonly, onBeforeUnmount } from "vue";
import { client } from "../lib/appwrite";
import { appwriteConfig } from "../lib/appwrite";
import { chatStateManager } from "../lib/chatStateManager";

export function useRealtimeMessages() {
  const messageSubscription = ref(null);
  const isSubscribed = ref(false);
  const processedMessageIds = new Set(); // Track processed message IDs

  // Get agent details from conversation instead of searching collections
  const fetchAgentDetails = async (agentId) => {
    try {
      console.log("🔍 Fetching agent details from conversation for:", agentId);

      // Get current conversation from chat state
      const currentState = chatStateManager.getState();
      let conversation = currentState.conversation;

      // If conversation has agent details, use them
      if (conversation && conversation.agentName) {
        console.log("✅ Found agent details in conversation:", {
          agentName: conversation.agentName,
          agentProfile: conversation.agentProfile,
        });

        return {
          agentName: conversation.agentName,
          avatar: conversation.agentProfile,
          agentId: agentId,
        };
      }

      // If not found, try to fetch fresh conversation data
      if (conversation && conversation.$id) {
        try {
          console.log("🔄 Fetching fresh conversation data...");
          const { databases } = await import("../lib/appwrite");
          const freshConversation = await databases.getDocument(
            appwriteConfig.databaseId,
            appwriteConfig.conversationCollectionId,
            conversation.$id
          );

          if (freshConversation && freshConversation.agentName) {
            console.log("✅ Found agent details in fresh conversation:", {
              agentName: freshConversation.agentName,
              agentProfile: freshConversation.agentProfile,
            });

            return {
              agentName: freshConversation.agentName,
              avatar: freshConversation.agentProfile,
              agentId: agentId,
            };
          }
        } catch (fetchError) {
          console.log("❌ Error fetching fresh conversation:", fetchError);
        }
      }

      // Fallback if conversation doesn't have agent details yet
      console.log("⚠️ No agent details found, using fallback");
      return {
        agentName: "Support Agent",
        avatar: null,
        agentId: agentId,
      };
    } catch (error) {
      console.error(
        "❌ Error fetching agent details from conversation:",
        error
      );
      return {
        agentName: "Support Agent",
        avatar: null,
        agentId: agentId,
      };
    }
  };

  const startMessageSubscription = (conversationId) => {
    if (!conversationId) {
      console.log("Cannot subscribe: missing conversation ID");
      return;
    }

    // Stop existing subscription first
    stopMessageSubscription();

    try {
      // Subscribe to new messages in the message collection
      const channelId = `databases.${appwriteConfig.databaseId}.collections.${appwriteConfig.messageCollectionId}.documents`;

      console.log(
        "🔔 Starting message subscription for conversation:",
        conversationId
      );
      console.log("🔔 Subscription channel:", channelId);

      messageSubscription.value = client.subscribe(
        channelId,
        async (response) => {
          console.log("📨 Real-time message update received:", {
            events: response.events,
            timestamp: response.timestamp,
            hasPayload: !!response.payload,
          });

          // Check if this is a new message creation
          const isNewMessage = response.events.some(
            (event) => event.includes("documents.") && event.includes(".create")
          );

          console.log("🔍 Is new message creation event?", isNewMessage);

          if (isNewMessage && response.payload) {
            const newMessage = response.payload;

            console.log("📨 NEW MESSAGE DETECTED:", {
              id: newMessage.$id,
              sender_type: newMessage.sender_type,
              conversation_id: newMessage.conversation_id,
              content_preview: newMessage.content?.substring(0, 50) + "...",
            });

            // ✅ FIRST CHECK: Prevent processing same message ID multiple times
            if (processedMessageIds.has(newMessage.$id)) {
              console.log(
                "🚫 Message already processed, skipping:",
                newMessage.$id
              );
              return;
            }

            console.log("🔎 Raw message payload:", {
              id: newMessage.$id,
              conversation_id: newMessage.conversation_id,
              sender_id: newMessage.sender_id,
              sender_type: newMessage.sender_type,
              content: newMessage.content?.substring(0, 100) + "...",
              message_type: newMessage.message_type,
              created_at: newMessage.created_at,
              // ✅ NEW: Log image data fields
              image_url: newMessage.image_url,
              image_file_id: newMessage.image_file_id,
              file_name: newMessage.file_name,
              mime_type: newMessage.mime_type,
            });

            // Check if this message belongs to our conversation
            const messageConversationId =
              typeof newMessage.conversation_id === "object"
                ? newMessage.conversation_id.$id || newMessage.conversation_id
                : newMessage.conversation_id;

            console.log("🔍 Comparing conversation IDs:", {
              messageConvId: messageConversationId,
              ourConvId: conversationId,
              match: messageConversationId === conversationId,
            });

            if (messageConversationId === conversationId) {
              console.log(
                "📨 ✅ Message belongs to our conversation:",
                newMessage.content?.substring(0, 50)
              );

              // ✅ Mark as processed immediately to prevent duplicates
              processedMessageIds.add(newMessage.$id);

              // Get current state to check for duplicates by content/timestamp
              const currentState = chatStateManager.getState();

              console.log("🔍 Current state check:", {
                currentMessageCount: currentState.messages.length,
                newMessageContent: newMessage.content?.substring(0, 50),
                newMessageSender: newMessage.sender_id,
              });

              // ✅ Additional duplicate check by content and sender
              const messageExists = currentState.messages.some(
                (msg) =>
                  msg.content === newMessage.content &&
                  msg.senderId === newMessage.sender_id &&
                  Math.abs(
                    new Date(msg.timestamp) - new Date(newMessage.created_at)
                  ) < 3000 // Within 3 seconds
              );

              if (messageExists) {
                console.log(
                  "🚫 Message already exists by content/sender, skipping duplicate"
                );
                return;
              }

              // ✅ FIXED: Create message with ALL fields from the original message
              let messageToAdd = {
                id: newMessage.$id,
                content: newMessage.content,
                timestamp: new Date(newMessage.created_at),
                senderId: newMessage.sender_id,
                messageType: newMessage.message_type || "text",

                // ✅ CRITICAL FIX: Copy ALL image/file fields
                image_url: newMessage.image_url,
                image_file_id: newMessage.image_file_id,
                file_name: newMessage.file_name,
                file_size: newMessage.file_size,
                mime_type: newMessage.mime_type,
                download_url: newMessage.download_url,
                message_type: newMessage.message_type,

                // 🆕 NEW: Copy FAQ fields
                faq_id: newMessage.faq_id,
                faq_question: newMessage.faq_question,
                faq_answer: newMessage.faq_answer,
                faq_category: newMessage.faq_category,

                // ✅ Copy other useful fields
                status: newMessage.status,
                is_read: newMessage.is_read,
                channel: newMessage.channel,
                created_at: newMessage.created_at,
              };

              // ✅ ENHANCED DEBUG: Handle sender_type correctly
              console.log(
                "🔍 Processing message with sender_type:",
                newMessage.sender_type
              );

              if (newMessage.sender_type === "agent") {
                // This is a human agent message
                messageToAdd.type = "agent";
                console.log(
                  "👤 Detected HUMAN AGENT message, fetching details..."
                );

                try {
                  const agentInfo = await fetchAgentDetails(
                    newMessage.sender_id
                  );
                  messageToAdd.agentInfo = agentInfo;
                  console.log("👤 Human agent message with info:", agentInfo);
                } catch (error) {
                  console.error("❌ Error fetching agent info:", error);
                  messageToAdd.agentInfo = {
                    agentName: "Support Agent",
                    avatar: null,
                    agentId: newMessage.sender_id,
                  };
                }
              } else if (newMessage.sender_type === "ai") {
                // ✅ ENHANCED DEBUG: Handle AI messages correctly
                messageToAdd.type = "ai";
                console.log("🤖 🎯 DETECTED AI MESSAGE - FULL DEBUG:", {
                  messageId: newMessage.$id,
                  content: newMessage.content?.substring(0, 100),
                  sender_id: newMessage.sender_id,
                  sender_type: newMessage.sender_type,
                  conversation_id: messageConversationId,
                  created_at: newMessage.created_at,
                  willAddToState: true,
                });

                // ✅ ADDITIONAL CHECK: Make sure we're not blocking AI messages
                const currentMessages = chatStateManager.getState().messages;
                console.log(
                  "🔍 Current message count before adding AI message:",
                  currentMessages.length
                );
                console.log(
                  "🔍 Last 3 messages in state:",
                  currentMessages.slice(-3).map((m) => ({
                    id: m.id,
                    type: m.type,
                    content: m.content?.substring(0, 50),
                  }))
                );

                // AI messages don't need agentInfo
              } else if (newMessage.sender_type === "customer") {
                // This is a customer message (maybe from another device/tab)
                messageToAdd.type = "customer";
                console.log("👤 Detected customer message");
              } else if (newMessage.sender_type === "system") {
                // System message
                messageToAdd.type = "system";
                console.log("⚙️ Detected system message");
              } else {
                // ✅ ENHANCED: Unknown types default to 'ai' if sender_id contains 'ai'
                if (
                  newMessage.sender_id &&
                  newMessage.sender_id.includes("ai")
                ) {
                  messageToAdd.type = "ai";
                  console.log(
                    '🤖 Unknown sender_type but sender_id contains "ai", treating as AI:',
                    {
                      sender_id: newMessage.sender_id,
                      sender_type: newMessage.sender_type,
                    }
                  );
                } else {
                  messageToAdd.type = "agent";
                  console.log("❓ Unknown sender_type, defaulting to agent:", {
                    sender_id: newMessage.sender_id,
                    sender_type: newMessage.sender_type,
                  });
                  messageToAdd.agentInfo = {
                    agentName: "Support Agent",
                    avatar: null,
                    agentId: newMessage.sender_id,
                  };
                }
              }

              // ✅ ENHANCED DEBUG: Log image data being processed
              if (
                newMessage.message_type === "image" ||
                newMessage.image_url ||
                newMessage.file_name
              ) {
                console.log("📷 ✅ PROCESSING IMAGE MESSAGE:", {
                  message_type: newMessage.message_type,
                  image_url: newMessage.image_url,
                  image_file_id: newMessage.image_file_id,
                  file_name: newMessage.file_name,
                  file_size: newMessage.file_size,
                  mime_type: newMessage.mime_type,
                  download_url: newMessage.download_url,
                });

                console.log("📷 ✅ MESSAGE TO ADD CONTAINS:", {
                  image_url: messageToAdd.image_url,
                  image_file_id: messageToAdd.image_file_id,
                  file_name: messageToAdd.file_name,
                  file_size: messageToAdd.file_size,
                  mime_type: messageToAdd.mime_type,
                  download_url: messageToAdd.download_url,
                });
              }

              // ✅ ENHANCED DEBUG: Before adding to state
              console.log("🚀 ABOUT TO ADD MESSAGE TO STATE:", {
                id: messageToAdd.id,
                type: messageToAdd.type,
                content: messageToAdd.content?.substring(0, 50) + "...",
                sender_id: messageToAdd.senderId,
                timestamp: messageToAdd.timestamp,
                hasImageData: !!(
                  messageToAdd.image_url || messageToAdd.file_name
                ),
              });

              // Add the message to state
              chatStateManager.addMessage(messageToAdd);

              console.log("✅ 🎉 MESSAGE ADDED TO CHAT STATE!");

              // ✅ VERIFY: Check if message was actually added
              const updatedState = chatStateManager.getState();
              console.log(
                "🔍 Message count after adding:",
                updatedState.messages.length
              );
              const lastMessage =
                updatedState.messages[updatedState.messages.length - 1];
              console.log("🔍 Last message in state:", {
                id: lastMessage?.id,
                type: lastMessage?.type,
                content: lastMessage?.content?.substring(0, 50),
                hasImageUrl: !!lastMessage?.image_url,
                hasFileName: !!lastMessage?.file_name,
              });

              // Emit events based on message type
              if (messageToAdd.type === "agent") {
                console.log("🤖 Human agent replied in real-time!");

                if (typeof window !== "undefined") {
                  window.dispatchEvent(
                    new CustomEvent("agent-message-received", {
                      detail: messageToAdd,
                    })
                  );
                }
              } else if (messageToAdd.type === "ai") {
                console.log(
                  "🤖 🎉 AI MESSAGE PROCESSED SUCCESSFULLY IN REAL-TIME!"
                );

                if (typeof window !== "undefined") {
                  window.dispatchEvent(
                    new CustomEvent("ai-message-received", {
                      detail: messageToAdd,
                    })
                  );
                }
              }
            } else {
              console.log("❌ Message NOT for our conversation:", {
                messageConvId: messageConversationId,
                ourConvId: conversationId,
              });
            }
          } else {
            console.log(
              "ℹ️ Real-time event but not a new message creation:",
              response.events
            );
          }
        }
      );

      isSubscribed.value = true;
      console.log(
        `✅ Subscribed to real-time messages for conversation: ${conversationId}`
      );
    } catch (error) {
      console.error("❌ Error subscribing to messages:", error);
      isSubscribed.value = false;
    }
  };

  const stopMessageSubscription = () => {
    if (messageSubscription.value) {
      console.log("🔇 Stopping message subscription");
      messageSubscription.value();
      messageSubscription.value = null;
      isSubscribed.value = false;

      // Clear processed message tracking when stopping
      processedMessageIds.clear();
      console.log("🗑️ Cleared processed message IDs");
    }
  };

  // Subscribe to chat state changes to auto-manage subscription
  const stateUnsubscribe = chatStateManager.subscribe((newState) => {
    // Check if conversation changed
    const currentConversationId = newState.conversation?.$id;

    if (currentConversationId && !isSubscribed.value) {
      // New conversation, start subscription
      console.log(
        "🔄 New conversation detected, starting subscription:",
        currentConversationId
      );
      startMessageSubscription(currentConversationId);
    } else if (!currentConversationId && isSubscribed.value) {
      // No conversation, stop subscription
      console.log("🔄 No conversation, stopping subscription");
      stopMessageSubscription();
    }
  });

  // Cleanup on component unmount
  onBeforeUnmount(() => {
    console.log("🧹 Cleaning up real-time message subscription");
    stopMessageSubscription();
    if (stateUnsubscribe) {
      stateUnsubscribe();
    }
  });

  return {
    startMessageSubscription,
    stopMessageSubscription,
    isSubscribed: readonly(isSubscribed),
  };
}
